import React from "react";
import countryList from "react-select-country-list";
import { CANDIDATE_KEY_DOCUMENTS } from "../../../../constants/app";
import useFileNameLength from "../../../../hooks/useFileNameLength";
import {
  ALTERNATIVE_POSITIONS,
  COMPLETE_YOUR_STUDIES,
  EARLY_CHILDHOOD_EDUCATION,
  OTHER_QUALIFICATIONS_LEVEL,
} from "../../../../models/Onboarding/onboarding-values";
import { useAppSelector as useSelector } from "../../../../store";
import {
  getEnumValue,
  normalizeQualificationKey,
  parseJSON,
} from "../../../../utils/dataParser";
import { completionDateDocTypes, formatDateToReadable, getDateLabel, getDocumentDate, getDocumentName, isIssuedType, isOneMonthBefore, validDateDocTypes } from "../../../../utils/documentHelper";
import { formatFileName } from "../../../../utils/stringUtils";
import CustomButton from "../../../UI/Buttons/CustomButton";
import Download from "../../../UI/Icons/Download";
import CandidateDocumentRenew from "../CandidateDocumentRenew";

const countryOptions = countryList().getData();


const getCountryName = (code: string): string => {
  const country = countryOptions.find((c) => c.value === code);
  return country?.label ?? code;
};

const Qualifications: React.FC = () => {
  const qualificationsData = useSelector(
    (state) => state.onboarding.userpersonaldetails?.qualifications
  );
  const userpersonaldetails = useSelector(
    (state) => state.onboarding.userpersonaldetails
  );
  const isPendingRequest = userpersonaldetails?.is_pending_request;

  const documents = (qualificationsData?.documents || [])
    .filter((doc) => CANDIDATE_KEY_DOCUMENTS.includes(doc.document_type))
    .sort((a, b) => { // To maintain order as per CANDIDATE_KEY_DOCUMENTS
      const indexA = CANDIDATE_KEY_DOCUMENTS.indexOf(a.document_type);
      const indexB = CANDIDATE_KEY_DOCUMENTS.indexOf(b.document_type);

      // If both are in the preferred list, sort by their index
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // If only one is in the list, it comes first
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      // Otherwise, keep original order
      return 0;
    });
  const qualificationAcceptOptions = parseJSON(
    qualificationsData?.qualification_accept_options
  );
  const alternativePositions = parseJSON(qualificationsData?.alternative_positions);
  const educatorTeacherPositions = (() => {
    const val = qualificationsData?.educator_teacher_positions;
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try { return JSON.parse(val); } catch { return []; }
  })();
  const { maxLength, preserveEndChars } = useFileNameLength();

  return (
    <>
      <div className="d-flex flex-column gap-10">
        <div className="d-flex flex-column gap-5">
          <div className="fs-13 fw-400 text-grey-medium">
            Which qualification do you have in Early Childhood Education?
          </div>
          <div className="fs-16 fw-400 text-black">
            {getEnumValue(
              EARLY_CHILDHOOD_EDUCATION,
              normalizeQualificationKey(qualificationsData?.qualification)
            )}
          </div>

          {qualificationsData?.qualification?.toLowerCase() ===
            "currently_studying" ? (
            <div>
              { qualificationsData?.currently_studying_option ===
                "diploma_equivalent"
                  ? "I can provide a course progression letter confirming I meet the equivalent requirements of a Diploma level."
                  : "I can provide a course progression letter confirming I meet the equivalent requirements of a Certificate III level."}
            </div>
          ) : "" }
        </div>
        <div className="d-flex flex-column gap-5">
          <div className="fs-13 fw-400 text-grey-medium">
            Where did you complete your studies in Early Childhood Education?
          </div>
          <div className="fs-16 fw-400 text-black">
            <>
              {qualificationsData?.studies_completed_location?.toLowerCase() ===
              COMPLETE_YOUR_STUDIES.OTHER.toLowerCase() ? (
                <>
                  <div>
                    {getCountryName(
                      qualificationsData?.other_study_selected_country
                    )}
                  </div>
                  {qualificationsData?.acecqa_approved ? (
                    <div>I confirm my qualification is ACECQA approved</div>
                  ) : ""}
                </>
              ) : (
                <div>
                  {getEnumValue(
                    COMPLETE_YOUR_STUDIES,
                    qualificationsData?.studies_completed_location
                  )}
                </div>
              )}
            </>
          </div>
        </div>

        { qualificationsData?.qualification === "bachelor_or_degree" || qualificationsData?.qualification === "post_graduate_diploma" ? (
          <div className="d-flex flex-column gap-5">
            <div className="fs-13 fw-400 text-grey-medium">
              What other qualification levels in Early Childhood Education would you be open to accepting?
            </div>
            <div className="fs-16 fw-400 text-black">
              { qualificationAcceptOptions?.length > 0 
                ? qualificationAcceptOptions
                .map((item: string) =>
                  getEnumValue(OTHER_QUALIFICATIONS_LEVEL, item)
                )
                .join(", ") 
                : "No levels selected"
              }
            </div>
          </div>
        ) : null }
        
        { qualificationsData?.qualification === "diploma" ? (
            <div className="d-flex flex-column gap-5">
              <div className="fs-13 fw-400 text-grey-medium">
                Would you be open to accepting jobs at a Certificate III Educator level?
              </div>
              <div className="fs-16 fw-400 text-black">
                {qualificationsData?.diploma_jobs_at_certificate_level}
              </div>
            </div>
          ) : "" }
      </div>
      <>
        <div className="w-100 h-1px bg-light-grey"></div>
        <div className="fs-16 fw-700 text-black">Position Types</div>
        {
          qualificationsData?.qualification !== "no_qualifications" && (
            <div className="d-flex flex-column gap-5">
              <div className="fs-13 fw-400 text-grey-medium">
              Educator/Teacher Positions:
              </div>
              <div className="fs-16 fw-400 text-black">
                {educatorTeacherPositions?.length > 0 ? educatorTeacherPositions
                  .map((item: string) =>
                    getEnumValue(ALTERNATIVE_POSITIONS, item)
                  )
                  .join(", ") : "No positions selected"}
              </div>
            </div>
          )
        }
        
        <div className="d-flex flex-column gap-5">
          <div className="fs-13 fw-400 text-grey-medium">
           Alternative Positions:
          </div>
          <div className="fs-16 fw-400 text-black">
            {alternativePositions?.length > 0 ? alternativePositions.filter((item: string) => !["1", "2", "5"].includes(item))
              .map((item: string) =>
                getEnumValue(ALTERNATIVE_POSITIONS, item)
              )
              .join(", ") : "No positions selected"}
          </div>
        </div>
      </>

      <div className="w-100 h-1px bg-light-grey"></div>

      <div className="fs-16 fw-700 text-black">Key Documents</div>
      <div className="d-flex flex-column gap-20">
        {documents.map((doc, idx: number) => (
          <>
            <div className="d-flex flex-column gap-10" key={idx}>
              <div className="fs-13 fw-400 text-grey-medium text-capitalize">
                {getDocumentName(doc.document_type)}
              </div>
              <div className="d-flex">
                <a href={doc.file_path} target="_blank" rel="noopener noreferrer">
                  <CustomButton
                    variant={"primary"}
                    type="button"
                    className="btn-theme bg-off-white text-black"
                  >
                    <Download />
                    {}
                     {formatFileName(doc.file_name ?? "", maxLength, preserveEndChars)}
                  </CustomButton>
                </a>
              </div>
              { doc.document_type !== "ACECQA_assessment" && doc.document_type !== "permanent_residency_visa" && doc.document_type !== "course_progression_letter" ? 
                <div className="d-flex flex-row gap-5">
                  <div className="fs-13 fw-400 text-grey-dark">
                    {getDateLabel(doc.document_type)}:{" "}
                  </div>
                  <span
                    className={`fs-13 ${
                      validDateDocTypes.includes(doc.document_type) &&
                      isOneMonthBefore(doc?.validity_date ?? "")
                        ? "fw-700 text-alert"
                        : "fw-400 text-grey-dark"
                    }`}
                  >
                    {isIssuedType(doc.document_type) ? (
                      <>
                        {formatDateToReadable(doc.issued_date) || "Not provided"} | Valid until:{" "}
                        <span className={`${
                          isOneMonthBefore(doc?.validity_date)
                            ? "fw-700 text-alert"
                            : "fw-400 text-grey-dark"
                        }`}>
                          {formatDateToReadable(doc?.validity_date) || "Not provided"}
                        </span>
                      </>
                    ) : (
                      formatDateToReadable(getDocumentDate(doc)) || "Not provided"
                    )}
                  </span>
                </div> 
              : "" }
              {!isPendingRequest && !completionDateDocTypes.includes(doc.document_type) && isOneMonthBefore(doc?.validity_date)
                ? <CandidateDocumentRenew documentName={doc.document_type}/>
                : null}
            </div>
            {/* Add separator only if it's not the last item */}
            {idx < documents.length - 1 && (
              <div className="bg-off-white min-h-1px"></div>
            )}
          </>
        ))}
      </div>
    </>
  );
};

export default Qualifications;
